import { useState, useEffect, useMemo, useRef, FormEvent } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { getWineDisplayName } from './utils';
import {
  Cellar,
  Wine,
  WineHistory,
  WineFormData,
  DrinkFormData,
  CellarFormData,
  CellarMember,
  CellarInvitation,
  CellarSuggestions,
  Toast as ToastType,
  GroupedHistoryEntry,
  HistoryOccasion,
  CanonicalWine,
  QuickAddFormData
} from './types';
import {
  WINE_COUNTRIES,
  WINE_REGIONS,
  COMMON_VARIETALS,
  getAllRegions,
  getAllAppellations,
  getRegionsForCountry,
  getAppellationsForCountry
} from './data/wineData';
import {
  AddScoreModal,
  AuthForm,
  BottomTabBar,
  CanonicalWineDetail,
  CellarForm,
  CellarList,
  DrinkModal,
  FilterDrawer,
  HistoryModal,
  HistoryTab,
  MembersPanel,
  PendingInvitations,
  ProfileModal,
  ProfileTab,
  QuickAddModal,
  StatsTab,
  Toast,
  WineDetail,
  WineForm,
  WineList,
  WineSearchScreen
} from './components';
import type { TabId } from './components';

const initialWineForm: WineFormData = {
  wine_name: '',
  producer: '',
  vintage: '',
  color: 'red',
  wine_type: 'still',
  country: '',
  region: '',
  appellation: '',
  varietal: '',
  bottle_size: '750ml',
  quantity: 1,
  purchase_price: '',
  cellar_location: '',
  drink_from: '',
  drink_to: '',
  my_rating: '',
  tasting_notes: ''
};

const initialDrinkForm: DrinkFormData = {
  date_drank: new Date().toISOString().split('T')[0],
  occasion_notes: '',
  rating: '',
  quantity: 1
};

const initialCellarForm: CellarFormData = {
  name: '',
  description: ''
};

export function App() {
  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Data state
  const [cellars, setCellars] = useState<Cellar[]>([]);
  const [currentCellar, setCurrentCellar] = useState<Cellar | null>(null);
  const [wines, setWines] = useState<Wine[]>([]);
  const [wineHistory, setWineHistory] = useState<WineHistory[]>([]);
  const [members, setMembers] = useState<CellarMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<CellarInvitation[]>([]);
  const [cellarPendingInvites, setCellarPendingInvites] = useState<CellarInvitation[]>([]);

  // UI state
  const [activeTab, setActiveTab] = useState<TabId>('cellar');
  const [showCellarForm, setShowCellarForm] = useState(false);
  const [showWineSearch, setShowWineSearch] = useState(false);
  const [showWineForm, setShowWineForm] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showDrinkModal, setShowDrinkModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [showPendingInvites, setShowPendingInvites] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingCellar, setEditingCellar] = useState<Cellar | null>(null);
  const [editingWine, setEditingWine] = useState<Wine | null>(null);
  const [drinkingWine, setDrinkingWine] = useState<Wine | null>(null);
  const [scoringWine, setScoringWine] = useState<Wine | null>(null);
  const [selectedWine, setSelectedWine] = useState<Wine | null>(null);
  const [selectedCanonicalWine, setSelectedCanonicalWine] = useState<CanonicalWine | null>(null);
  const [viewingHistoryWine, setViewingHistoryWine] = useState<CanonicalWine | null>(null);
  const [refreshingRatings, setRefreshingRatings] = useState(false);
  const [isPageRefreshing, setIsPageRefreshing] = useState(false);
  const [addFromHistoryCanonicalId, setAddFromHistoryCanonicalId] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterColor, setFilterColor] = useState('');
  const [filterVarietal, setFilterVarietal] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [sortBy, setSortBy] = useState('name');

  // Form state
  const [wineForm, setWineForm] = useState<WineFormData>(initialWineForm);
  const [drinkForm, setDrinkForm] = useState<DrinkFormData>(initialDrinkForm);
  const [cellarForm, setCellarForm] = useState<CellarFormData>(initialCellarForm);

  // Toast state
  const [toast, setToast] = useState<ToastType | null>(null);

  // Helper functions
  const showToastMessage = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Computed values
  const isOwner = currentCellar?.owner_id === user?.id;

  const stats = useMemo(() => {
    const total = wines.reduce((sum, w) => sum + w.quantity, 0);
    const value = wines.reduce((sum, w) => sum + (w.purchase_price || 0) * w.quantity, 0);
    const unique = wines.length;
    return { total, value, unique };
  }, [wines]);

  // Grouped history for History and Stats tabs
  const groupedHistory = useMemo<GroupedHistoryEntry[]>(() => {
    const groups: Record<string, GroupedHistoryEntry> = {};
    
    wineHistory.forEach(entry => {
      const key = `${(entry.wine_name || '').toLowerCase()}-${(entry.producer || '').toLowerCase()}-${entry.vintage || ''}`;
      if (!groups[key]) {
        groups[key] = {
          wine_name: entry.wine_name,
          producer: entry.producer,
          vintage: entry.vintage,
          country: entry.country,
          region: entry.region,
          appellation: entry.appellation,
          varietal: entry.varietal,
          color: entry.color,
          wine_type: entry.wine_type,
          bottle_size: entry.bottle_size,
          purchase_price: entry.purchase_price,
          canonical_wine_id: entry.canonical_wine_id,
          occasions: [],
          total_consumed: 0
        };
      }
      if (entry.canonical_wine_id && !groups[key].canonical_wine_id) {
        groups[key].canonical_wine_id = entry.canonical_wine_id;
      }
      groups[key].occasions.push({
        id: entry.id,
        date_drank: entry.date_drank,
        occasion_notes: entry.occasion_notes,
        rating: entry.rating,
        quantity_consumed: 1
      });
      groups[key].total_consumed += 1;
    });

    // Sort by most recent
    return Object.values(groups).sort((a, b) => {
      const aDate = Math.max(...a.occasions.map(o => new Date(o.date_drank).getTime()));
      const bDate = Math.max(...b.occasions.map(o => new Date(o.date_drank).getTime()));
      return bDate - aDate;
    });
  }, [wineHistory]);

  // Available filter options from current wines
  const filterOptions = useMemo(() => {
    const varietals = [...new Set(wines.map(w => w.varietal).filter(Boolean) as string[])].sort();
    const countries = [...new Set(wines.map(w => w.country).filter(Boolean) as string[])].sort();
    return { varietals, countries };
  }, [wines]);

  // Filtered and sorted wines
  const filteredWines = useMemo(() => {
    let result = wines.filter(wine => {
      const matchesSearch = !searchTerm || 
        wine.wine_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wine.producer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wine.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wine.varietal?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesColor = !filterColor || wine.color === filterColor;
      const matchesVarietal = !filterVarietal || wine.varietal === filterVarietal;
      const matchesCountry = !filterCountry || wine.country === filterCountry;
      return matchesSearch && matchesColor && matchesVarietal && matchesCountry;
    });

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'vintage-desc':
          return (b.vintage || 0) - (a.vintage || 0);
        case 'vintage-asc':
          return (a.vintage || 0) - (b.vintage || 0);
        case 'recent':
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        case 'price-desc':
          return (b.purchase_price || 0) - (a.purchase_price || 0);
        case 'price-asc':
          return (a.purchase_price || 0) - (b.purchase_price || 0);
        default: // name
          return (a.wine_name || '').localeCompare(b.wine_name || '');
      }
    });

    return result;
  }, [wines, searchTerm, filterColor, filterVarietal, filterCountry, sortBy]);

  const activeFilterCount = [filterColor, filterVarietal, filterCountry, sortBy !== 'name' ? sortBy : ''].filter(Boolean).length;

  // Build suggestions for autocomplete
  const cellarSuggestions = useMemo<CellarSuggestions>(() => {
    const producers = [...new Set(wines.map(w => w.producer).filter(Boolean) as string[])];
    const existingVarietals = wines.map(w => w.varietal).filter(Boolean) as string[];
    const varietals = [...new Set([...COMMON_VARIETALS, ...existingVarietals])].sort();
    
    const existingCountries = wines.map(w => w.country).filter(Boolean) as string[];
    const countries = [...new Set([...WINE_COUNTRIES, ...existingCountries])].sort();

    // Smart region/appellation filtering based on selected country
    const selectedCountry = wineForm.country;
    let regions: string[];
    let appellations: string[];

    if (selectedCountry && WINE_REGIONS[selectedCountry]) {
      const existingRegions = wines.map(w => w.region).filter(Boolean) as string[];
      regions = [...new Set([...getRegionsForCountry(selectedCountry), ...existingRegions])].sort();
      const existingAppellations = wines.map(w => w.appellation).filter(Boolean) as string[];
      appellations = [...new Set([...getAppellationsForCountry(selectedCountry), ...existingAppellations])].sort();
    } else {
      const existingRegions = wines.map(w => w.region).filter(Boolean) as string[];
      regions = [...new Set([...getAllRegions(), ...existingRegions])].sort();
      const existingAppellations = wines.map(w => w.appellation).filter(Boolean) as string[];
      appellations = [...new Set([...getAllAppellations(), ...existingAppellations])].sort();
    }

    const locations = [...new Set(wines.map(w => w.cellar_location).filter(Boolean) as string[])];

    return { producers, varietals, countries, regions, appellations, locations };
  }, [wines, wineForm.country]);

  // Track if loading finished
  const loadingFinished = useRef(false);
  const [showRefreshMessage, setShowRefreshMessage] = useState(false);

  // Auth effect
  useEffect(() => {
    const init = async () => {
      try {
        console.log('[App] Initializing...');
        
        // Get the session with timeout
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Session timeout')), 8000)
        );
        
        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          timeoutPromise
        ]);
        
        const { data: { session }, error } = sessionResult as Awaited<ReturnType<typeof supabase.auth.getSession>>;
        
        if (error) throw error;
        
        if (session?.user) {
          console.log('[App] User found, loading data...');
          setUser(session.user);
          
          // Load data with timeout - don't block if it fails
          try {
            await Promise.race([
              Promise.all([
                loadUserProfile(session.user),
                loadCellars(session.user),
                loadPendingInvitations(session.user)
              ]),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Data load timeout')), 10000))
            ]);
          } catch (dataError) {
            console.error('[App] Data load error:', dataError);
            // Continue anyway - user is logged in
          }
        }
        
        console.log('[App] Init complete');
        loadingFinished.current = true;
        setLoading(false);
        sessionStorage.removeItem('cellarlog_refreshed');
      } catch (error) {
        console.error('[App] Init error:', error);
        // Show refresh message if timeout
        if (error instanceof Error && error.message.includes('timeout')) {
          setShowRefreshMessage(true);
        }
        loadingFinished.current = true;
        setLoading(false);
      }
    };
    
    init();
    
    // Listen for future auth changes (login/logout)
    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('[Auth] State change:', _event);
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      
      if (currentUser && _event === 'SIGNED_IN') {
        try {
          // Set a timeout for data loading
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Load timeout')), 10000)
          );
          
          await Promise.race([
            Promise.all([
              loadUserProfile(currentUser),
              loadCellars(currentUser),
              loadPendingInvitations(currentUser)
            ]),
            timeoutPromise
          ]);
          
          // Scroll to top on mobile after login
          window.scrollTo(0, 0);
        } catch (error) {
          console.error('Error loading data after sign in:', error);
          // Don't block - user is signed in, they can retry
        }
      } else if (_event === 'SIGNED_OUT') {
        setCellars([]);
        setCurrentCellar(null);
        setWines([]);
        setUsername(null);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // Load wines when cellar changes
  useEffect(() => {
    if (currentCellar) {
      loadWines();
      loadMembers();
      loadCellarPendingInvites();
      loadWineHistory();
    }
  }, [currentCellar]);

  // API functions

  async function loadUserProfile(currentUser: User) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('user_id', currentUser.id)
        .single();
      
      if (!error && data?.username) {
        setUsername(data.username);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  async function loadCellars(currentUser: User) {
    try {
      // Add timeout to detect hung queries
      const queryPromise = supabase
        .from('cellars')
        .select('*')
        .eq('owner_id', currentUser.id);
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Query timeout')), 3000)
      );
      
      const { data: ownedCellars, error: ownedError } = await Promise.race([queryPromise, timeoutPromise]) as any;
      
      if (ownedError) throw ownedError;

      const { data: sharedMemberships, error: sharedError } = await supabase
        .from('cellar_members')
        .select('cellar_id')
        .eq('user_id', currentUser.id);
      
      if (sharedError) throw sharedError;

      let sharedCellars: Cellar[] = [];
      if (sharedMemberships && sharedMemberships.length > 0) {
        const cellarIds = sharedMemberships.map(m => m.cellar_id);
        const { data: cellarsData, error: cellarsError } = await supabase
          .from('cellars')
          .select('*')
          .in('id', cellarIds);
        
        if (cellarsError) throw cellarsError;
        sharedCellars = cellarsData || [];
      }

      const allCellars = [...(ownedCellars || []), ...sharedCellars];
      setCellars(allCellars);
    } catch (error) {
      console.error('Error loading cellars:', error);
      throw error;
    }
  }

  async function loadPendingInvitations(currentUser: User) {
    try {
      const { data, error } = await supabase
        .from('cellar_invitations')
        .select('*, cellars(*)')
        .eq('invitee_email', currentUser.email)
        .eq('status', 'pending');
      
      if (error) throw error;
      setPendingInvitations(data || []);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  }

  async function loadWines() {
    if (!currentCellar || !user) return;
    
    try {
      // Load wines
      const { data: winesData, error: winesError } = await supabase
        .from('wines')
        .select('*')
        .eq('cellar_id', currentCellar.id)
        .order('wine_name', { ascending: true });
      
      if (winesError) throw winesError;
      
      // Load critic scores for all wines in this cellar
      const wineIds = (winesData || []).map(w => w.id);
      const canonicalIds = (winesData || [])
        .map(w => w.canonical_wine_id)
        .filter(Boolean) as string[];
      
      let scoresData: any[] = [];
      
      if (wineIds.length > 0) {
        const { data: scores, error: scoresError } = await supabase
          .from('wine_critic_scores')
          .select('*')
          .in('wine_id', wineIds);
        
        if (scoresError) {
          console.error('Error loading critic scores:', scoresError);
        } else {
          scoresData = scores || [];
        }
      }
      
      // Load user's personal ratings for these wines
      let userRatingsData: any[] = [];
      if (canonicalIds.length > 0) {
        const { data: userRatings, error: userRatingsError } = await supabase
          .from('user_wine_ratings')
          .select('*')
          .eq('user_id', user.id)
          .in('canonical_wine_id', canonicalIds);
        
        if (userRatingsError) {
          console.error('Error loading user ratings:', userRatingsError);
        } else {
          userRatingsData = userRatings || [];
        }
      }
      
      // Load CellarLog scores (all ratings for these canonical wines)
      let allRatingsData: any[] = [];
      if (canonicalIds.length > 0) {
        const { data: allRatings, error: allRatingsError } = await supabase
          .from('user_wine_ratings')
          .select('canonical_wine_id, rating')
          .in('canonical_wine_id', canonicalIds);
        
        if (allRatingsError) {
          console.error('Error loading all ratings:', allRatingsError);
        } else {
          allRatingsData = allRatings || [];
        }
      }
      
      // Calculate CellarLog scores per canonical wine
      const cellarlogScores: Record<string, { avg: number; count: number }> = {};
      canonicalIds.forEach(cid => {
        const ratings = allRatingsData.filter(r => r.canonical_wine_id === cid);
        if (ratings.length > 0) {
          const sum = ratings.reduce((acc, r) => acc + r.rating, 0);
          cellarlogScores[cid] = {
            avg: Math.round((sum / ratings.length) * 10) / 10,
            count: ratings.length
          };
        }
      });
      
      // Attach scores and ratings to wines
      const winesWithScores = (winesData || []).map(wine => {
        const userRating = wine.canonical_wine_id 
          ? userRatingsData.find(r => r.canonical_wine_id === wine.canonical_wine_id)
          : null;
        const cellarlogScore = wine.canonical_wine_id 
          ? cellarlogScores[wine.canonical_wine_id]
          : null;
        
        return {
          ...wine,
          critic_scores: scoresData.filter(s => s.wine_id === wine.id),
          user_rating: userRating?.rating ?? wine.my_rating, // Fall back to legacy my_rating
          cellarlog_score: cellarlogScore?.avg,
          cellarlog_rating_count: cellarlogScore?.count
        };
      });
      
      setWines(winesWithScores);
    } catch (error) {
      console.error('Error loading wines:', error);
    }
  }

  async function loadMembers() {
    if (!currentCellar) return;
    
    try {
      const { data, error } = await supabase
        .from('cellar_members')
        .select('*')
        .eq('cellar_id', currentCellar.id);
      
      if (error) throw error;
      
      const membersList: CellarMember[] = [
        { id: 'owner', cellar_id: currentCellar.id, user_id: currentCellar.owner_id, role: 'owner', created_at: '' },
        ...(data || [])
      ];
      
      setMembers(membersList);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  }

  async function loadCellarPendingInvites() {
    if (!currentCellar) return;
    
    try {
      const { data, error } = await supabase
        .from('cellar_invitations')
        .select('*')
        .eq('cellar_id', currentCellar.id)
        .eq('status', 'pending');
      
      if (error) throw error;
      setCellarPendingInvites(data || []);
    } catch (error) {
      console.error('Error loading cellar invites:', error);
    }
  }

  async function loadWineHistory() {
    console.log('[History Load] Starting, currentCellar:', currentCellar?.id);
    if (!currentCellar) {
      console.log('[History Load] No currentCellar, skipping');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('wine_history')
        .select('*')
        .eq('cellar_id', currentCellar.id)
        .order('date_drank', { ascending: false });
      
      console.log('[History Load] Result:', { count: data?.length, error });
      
      if (error) throw error;
      setWineHistory(data || []);
    } catch (error) {
      console.error('Error loading wine history:', error);
    }
  }

  async function handlePageRefresh() {
    if (!user) return;
    setIsPageRefreshing(true);
    try {
      await Promise.all([
        loadCellars(user),
        currentCellar ? loadWines() : Promise.resolve(),
        currentCellar ? loadMembers() : Promise.resolve(),
        loadWineHistory()
      ]);
      showToastMessage('Refreshed');
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setIsPageRefreshing(false);
    }
  }

  // Action handlers
  async function handleSignOut() {
    await supabase.auth.signOut();
    setUser(null);
    setCellars([]);
    setCurrentCellar(null);
    setWines([]);
  }

  async function handleDeleteAccount() {
    if (!user) return;
    
    try {
      // Delete user profile
      await supabase
        .from('user_profiles')
        .delete()
        .eq('user_id', user.id);

      // Delete cellars owned by user (this will cascade to wines, members, etc.)
      await supabase
        .from('cellars')
        .delete()
        .eq('owner_id', user.id);

      // Remove user from shared cellars
      await supabase
        .from('cellar_members')
        .delete()
        .eq('user_id', user.id);

      // Sign out
      await supabase.auth.signOut();
      
      setUser(null);
      setCellars([]);
      setCurrentCellar(null);
      setWines([]);
      setShowProfileModal(false);
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Error deleting account: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async function handleDeleteCellar() {
    if (!currentCellar || !user || currentCellar.owner_id !== user.id) return;
    
    try {
      // Delete all wines in the cellar
      await supabase
        .from('wines')
        .delete()
        .eq('cellar_id', currentCellar.id);

      // Delete all members
      await supabase
        .from('cellar_members')
        .delete()
        .eq('cellar_id', currentCellar.id);

      // Delete the cellar
      const { error } = await supabase
        .from('cellars')
        .delete()
        .eq('id', currentCellar.id);

      if (error) throw error;

      setCellars(cellars.filter(c => c.id !== currentCellar.id));
      setCurrentCellar(null);
      setShowCellarForm(false);
      setEditingCellar(null);
      showToastMessage('Cellar deleted');
    } catch (error) {
      console.error('Error deleting cellar:', error);
      alert('Error deleting cellar: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async function handleSubmitCellar(e: FormEvent) {
    e.preventDefault();
    if (!user) return;

    try {
      if (editingCellar) {
        // Update existing cellar
        const { error } = await supabase
          .from('cellars')
          .update({ name: cellarForm.name, description: cellarForm.description })
          .eq('id', editingCellar.id);
        
        if (error) throw error;
        
        const updatedCellar = { ...editingCellar, name: cellarForm.name, description: cellarForm.description };
        setCellars(cellars.map(c => c.id === editingCellar.id ? updatedCellar : c));
        if (currentCellar?.id === editingCellar.id) {
          setCurrentCellar(updatedCellar);
        }
        showToastMessage('Cellar updated');
      } else {
        // Create new cellar
        const { data, error } = await supabase
          .from('cellars')
          .insert([{ name: cellarForm.name, description: cellarForm.description, owner_id: user.id }])
          .select()
          .single();
        
        if (error) throw error;
        
        setCellars([...cellars, data]);
        setCurrentCellar(data);
        showToastMessage('Cellar created');
      }
      
      setCellarForm(initialCellarForm);
      setEditingCellar(null);
      setShowCellarForm(false);
    } catch (error) {
      console.error('Error saving cellar:', error);
      alert('Error saving cellar: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async function handleSubmitWine(e: FormEvent, scanResults?: { scores: { critic_name: string; score: number }[]; notes?: string }) {
    e.preventDefault();
    if (!currentCellar) {
      alert('Please select or create a cellar first');
      return;
    }

    const criticScores = scanResults?.scores || [];
    const criticNotes = scanResults?.notes || null;

    try {
      const wineData = {
        wine_name: wineForm.wine_name || null,
        producer: wineForm.producer,
        vintage: wineForm.vintage ? parseInt(wineForm.vintage) : null,
        color: wineForm.color,
        wine_type: wineForm.wine_type,
        country: wineForm.country || null,
        region: wineForm.region || null,
        appellation: wineForm.appellation || null,
        varietal: wineForm.varietal || null,
        bottle_size: wineForm.bottle_size,
        quantity: wineForm.quantity,
        purchase_price: wineForm.purchase_price ? parseFloat(wineForm.purchase_price) : null,
        cellar_location: wineForm.cellar_location || null,
        drink_from: wineForm.drink_from ? parseInt(wineForm.drink_from) : null,
        drink_to: wineForm.drink_to ? parseInt(wineForm.drink_to) : null,
        my_rating: wineForm.my_rating ? parseInt(wineForm.my_rating) : null,
        tasting_notes: wineForm.tasting_notes || null,
        critic_notes: criticNotes,
        cellar_id: currentCellar.id
      };

      if (editingWine) {
        const { error } = await supabase
          .from('wines')
          .update(wineData)
          .eq('id', editingWine.id);
        
        if (error) throw error;
        
        // Save user rating if provided and wine has canonical_wine_id
        if (wineForm.my_rating && editingWine.canonical_wine_id) {
          await saveUserRating(editingWine.canonical_wine_id, parseInt(wineForm.my_rating));
        }
        
        await loadWines();
        resetWineForm();
        showToastMessage('Wine updated');
      } else {
        // Check for duplicates
        const normalizedName = (wineData.wine_name || '').toLowerCase().trim();
        const normalizedProducer = (wineData.producer || '').toLowerCase().trim();
        const normalizedVintage = wineData.vintage ? String(wineData.vintage) : '';
        const normalizedSize = (wineData.bottle_size || '750ml').toLowerCase().trim();

        const existingWine = wines.find(w => {
          const wName = (w.wine_name || '').toLowerCase().trim();
          const wProducer = (w.producer || '').toLowerCase().trim();
          const wVintage = w.vintage ? String(w.vintage) : '';
          const wSize = (w.bottle_size || '750ml').toLowerCase().trim();
          
          return wName === normalizedName &&
                 wProducer === normalizedProducer &&
                 wVintage === normalizedVintage &&
                 wSize === normalizedSize;
        });

        if (existingWine) {
          const newTotal = existingWine.quantity + wineData.quantity;
          const choice = window.confirm(
            `This wine already exists in your cellar:\n\n` +
            `${existingWine.wine_name} ${existingWine.vintage || ''}\n` +
            `Current quantity: ${existingWine.quantity} bottle${existingWine.quantity > 1 ? 's' : ''}\n\n` +
            `Add ${wineData.quantity} bottle${wineData.quantity > 1 ? 's' : ''} to existing entry (total: ${newTotal})?\n\n` +
            `Click OK to add to existing, or Cancel to create a new entry.`
          );

          if (choice) {
            // Update quantity and price
            const updateData: { quantity: number; purchase_price?: number | null } = { 
              quantity: newTotal 
            };
            if (wineData.purchase_price) {
              updateData.purchase_price = wineData.purchase_price;
            }
            
            const { error } = await supabase
              .from('wines')
              .update(updateData)
              .eq('id', existingWine.id);
            
            if (error) throw error;
            
            // Also add any critic scores to the existing wine
            if (criticScores && criticScores.length > 0) {
              await saveCriticScores(existingWine.id, criticScores);
            }
            
            await loadWines();
            resetWineForm();
            showToastMessage(`Added ${wineData.quantity} bottle${wineData.quantity > 1 ? 's' : ''} (now ${newTotal} total)`);
            return;
          }
        }

        const { data: newWine, error } = await supabase
          .from('wines')
          .insert([wineData])
          .select()
          .single();
        
        if (error) throw error;
        
        // Create or find canonical wine for new entries
        if (newWine) {
          // Use canonical_wine_id from history if available, otherwise find/create
          let canonicalWineId = addFromHistoryCanonicalId;
          
          if (!canonicalWineId) {
            canonicalWineId = await findOrCreateCanonicalWine({
              wine_name: wineData.wine_name,
              producer: wineData.producer || '',
              vintage: wineData.vintage,
              color: wineData.color,
              wine_type: wineData.wine_type,
              country: wineData.country,
              region: wineData.region,
              appellation: wineData.appellation,
              varietal: wineData.varietal,
              bottle_size: wineData.bottle_size,
              critic_notes: criticNotes
            });
          }

          if (canonicalWineId) {
            // Link the wine to the canonical wine
            await supabase
              .from('wines')
              .update({ canonical_wine_id: canonicalWineId })
              .eq('id', newWine.id);
            
            // If adding from history, copy critic scores from canonical wine
            if (addFromHistoryCanonicalId) {
              try {
                // Fetch canonical critic scores
                const { data: canonicalScores } = await supabase
                  .from('canonical_critic_scores')
                  .select('critic_name, score')
                  .eq('canonical_wine_id', canonicalWineId);
                
                if (canonicalScores && canonicalScores.length > 0) {
                  // Copy to wine_critic_scores
                  const scoreRecords = canonicalScores.map(s => ({
                    wine_id: newWine.id,
                    critic_name: s.critic_name,
                    score: s.score
                  }));
                  
                  await supabase
                    .from('wine_critic_scores')
                    .insert(scoreRecords);
                }
                
                // Also copy critic_notes from canonical wine
                const { data: canonicalWine } = await supabase
                  .from('canonical_wines')
                  .select('critic_notes')
                  .eq('id', canonicalWineId)
                  .single();
                  
                if (canonicalWine?.critic_notes) {
                  await supabase
                    .from('wines')
                    .update({ critic_notes: canonicalWine.critic_notes })
                    .eq('id', newWine.id);
                }
              } catch (err) {
                console.error('Error copying canonical scores:', err);
              }
            }
          }

          // Save critic scores to canonical wine
          if (criticScores && criticScores.length > 0) {
            await saveCriticScores(newWine.id, criticScores);
            // Also save to canonical scores if we have a canonical wine
            if (canonicalWineId) {
              await saveCanonicalCriticScores(canonicalWineId, criticScores);
            }
          }
          
          // Save user rating if provided
          if (wineForm.my_rating && canonicalWineId) {
            await saveUserRating(canonicalWineId, parseInt(wineForm.my_rating));
          }
          
          // Clear the history canonical id
          setAddFromHistoryCanonicalId(null);
        }
        
        await loadWines();
        resetWineForm();
        showToastMessage('Wine added');
      }
    } catch (error) {
      console.error('Error saving wine:', error);
      alert('Error saving wine: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }
  
  async function saveCriticScores(wineId: string, scores: { critic_name: string; score: number }[]) {
    const scoreRecords = scores.map(s => ({
      wine_id: wineId,
      critic_name: s.critic_name,
      score: s.score
    }));
    
    const { error } = await supabase
      .from('wine_critic_scores')
      .insert(scoreRecords);
    
    if (error) {
      console.error('Error saving critic scores:', error);
    }
  }

  async function saveUserRating(canonicalWineId: string, rating: number) {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('user_wine_ratings')
        .upsert({
          canonical_wine_id: canonicalWineId,
          user_id: user.id,
          rating: rating,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'canonical_wine_id,user_id'
        });
      
      if (error) {
        console.error('Error saving user rating:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in saveUserRating:', error);
      throw error;
    }
  }

  async function findOrCreateCanonicalWine(wineData: {
    wine_name: string | null;
    producer: string;
    vintage: number | null;
    color: string;
    wine_type: string;
    country: string | null;
    region: string | null;
    appellation: string | null;
    varietal: string | null;
    bottle_size: string;
    critic_notes?: string | null;
  }): Promise<string | null> {
    try {
      // First try to find an existing canonical wine
      let query = supabase
        .from('canonical_wines')
        .select('id')
        .ilike('producer', wineData.producer);
      
      // Handle optional wine_name
      if (wineData.wine_name) {
        query = query.ilike('wine_name', wineData.wine_name);
      } else {
        query = query.is('wine_name', null);
      }
      
      if (wineData.vintage) {
        query = query.eq('vintage', wineData.vintage);
      } else {
        query = query.is('vintage', null);
      }

      const { data: existing, error: searchError } = await query.maybeSingle();

      if (searchError) {
        console.error('Error searching canonical wines:', searchError);
        return null;
      }

      if (existing) {
        // Update critic_notes if we have them and the existing entry doesn't
        if (wineData.critic_notes) {
          await supabase
            .from('canonical_wines')
            .update({ critic_notes: wineData.critic_notes })
            .eq('id', existing.id)
            .is('critic_notes', null);
        }
        return existing.id;
      }

      // Create new canonical wine
      const { data: newCanonical, error: insertError } = await supabase
        .from('canonical_wines')
        .insert([{
          wine_name: wineData.wine_name,
          producer: wineData.producer,
          vintage: wineData.vintage,
          color: wineData.color,
          wine_type: wineData.wine_type,
          country: wineData.country,
          region: wineData.region,
          appellation: wineData.appellation,
          varietal: wineData.varietal,
          bottle_size: wineData.bottle_size,
          critic_notes: wineData.critic_notes,
          created_by: user?.id
        }])
        .select('id')
        .single();

      if (insertError) {
        console.error('Error creating canonical wine:', insertError);
        return null;
      }

      return newCanonical?.id || null;
    } catch (error) {
      console.error('Error in findOrCreateCanonicalWine:', error);
      return null;
    }
  }

  async function saveCanonicalCriticScores(canonicalWineId: string, scores: { critic_name: string; score: number }[]) {
    for (const score of scores) {
      const { error } = await supabase
        .from('canonical_critic_scores')
        .upsert({
          canonical_wine_id: canonicalWineId,
          critic_name: score.critic_name,
          score: score.score
        }, {
          onConflict: 'canonical_wine_id,critic_name'
        });
      
      if (error) {
        console.error('Error saving canonical critic score:', error);
      }
    }
  }

  const [cellarLogRating, setCellarLogRating] = useState<number | null>(null);

  async function handleRefreshRatings(wine: Wine) {
    if (refreshingRatings) return;
    
    setRefreshingRatings(true);
    showToastMessage('Fetching latest ratings...', 'success');
    
    try {
      // Call scan-label in refresh mode
      const response = await fetch('/.netlify/functions/scan-label', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refreshOnly: true,
          wineData: {
            producer: wine.producer,
            wine_name: wine.wine_name,
            varietal: wine.varietal,
            vintage: wine.vintage
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch ratings');
      }
      
      const data = await response.json();
      const now = new Date().toISOString();
      
      // Update critic notes if found
      const criticNotes = data.critic_notes || null;
      
      // Process critic scores
      const newScores = data.critic_scores || [];
      
      // Add Vivino from direct API if available
      if (data.vivino?.vivino_rating) {
        const existingVivino = newScores.find((s: { critic_name: string }) => s.critic_name === 'Vivino');
        if (!existingVivino) {
          newScores.push({ critic_name: 'Vivino', score: data.vivino.vivino_rating });
        }
      }
      
      // Update wine record - only update critic_notes if we found new ones
      const wineUpdateData: { ratings_updated_at: string; critic_notes?: string } = { 
        ratings_updated_at: now 
      };
      if (criticNotes) {
        wineUpdateData.critic_notes = criticNotes;
      }
      
      await supabase
        .from('wines')
        .update(wineUpdateData)
        .eq('id', wine.id);
      
      // Update canonical wine if linked
      if (wine.canonical_wine_id) {
        const canonicalUpdateData: { ratings_updated_at: string; critic_notes?: string; vivino_rating?: number } = { 
          ratings_updated_at: now 
        };
        if (criticNotes) {
          canonicalUpdateData.critic_notes = criticNotes;
        }
        if (data.vivino?.vivino_rating) {
          canonicalUpdateData.vivino_rating = data.vivino.vivino_rating;
        }
        
        await supabase
          .from('canonical_wines')
          .update(canonicalUpdateData)
          .eq('id', wine.canonical_wine_id);
        
        // Update/insert canonical critic scores
        for (const score of newScores) {
          // Check if score exists
          const { data: existing } = await supabase
            .from('canonical_critic_scores')
            .select('id')
            .eq('canonical_wine_id', wine.canonical_wine_id)
            .eq('critic_name', score.critic_name)
            .single();
          
          if (existing) {
            await supabase
              .from('canonical_critic_scores')
              .update({ score: score.score })
              .eq('id', existing.id);
          } else {
            await supabase
              .from('canonical_critic_scores')
              .insert({
                canonical_wine_id: wine.canonical_wine_id,
                critic_name: score.critic_name,
                score: score.score
              });
          }
        }
      }
      
      // Always update wine_critic_scores (for display in WineDetail)
      for (const score of newScores) {
        const { data: existing } = await supabase
          .from('wine_critic_scores')
          .select('id')
          .eq('wine_id', wine.id)
          .eq('critic_name', score.critic_name)
          .single();
        
        if (existing) {
          await supabase
            .from('wine_critic_scores')
            .update({ score: score.score })
            .eq('id', existing.id);
        } else {
          await supabase
            .from('wine_critic_scores')
            .insert({
              wine_id: wine.id,
              critic_name: score.critic_name,
              score: score.score
            });
        }
      }
      
      await loadWines();
      
      // Re-fetch the specific wine to get updated data
      const { data: updatedWineData } = await supabase
        .from('wines')
        .select('*')
        .eq('id', wine.id)
        .single();
      
      if (updatedWineData) {
        // Load its critic scores
        const { data: scoresData } = await supabase
          .from('wine_critic_scores')
          .select('*')
          .eq('wine_id', wine.id);
        
        const updatedWine = {
          ...updatedWineData,
          critic_scores: scoresData || []
        };
        setSelectedWine(updatedWine);
      }
      
      const scoresFound = newScores.length;
      showToastMessage(
        scoresFound > 0 
          ? `Found ${scoresFound} rating${scoresFound > 1 ? 's' : ''}${criticNotes ? ' + tasting note' : ''}!`
          : 'No new ratings found'
      );
      
    } catch (error) {
      console.error('Error refreshing ratings:', error);
      showToastMessage('Error fetching ratings', 'error');
    } finally {
      setRefreshingRatings(false);
    }
  }

  async function handleAddScore(wine: Wine) {
    setScoringWine(wine);
    
    // Calculate CellarLog community rating for this wine
    try {
      const { data, error } = await supabase
        .from('wines')
        .select('my_rating')
        .eq('producer', wine.producer || '')
        .eq('wine_name', wine.wine_name)
        .eq('vintage', wine.vintage || 0)
        .not('my_rating', 'is', null);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const ratings = data.map(w => w.my_rating).filter(r => r !== null);
        if (ratings.length > 0) {
          const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
          setCellarLogRating(avg);
        } else {
          setCellarLogRating(null);
        }
      } else {
        setCellarLogRating(null);
      }
    } catch (error) {
      console.error('Error fetching CellarLog rating:', error);
      setCellarLogRating(null);
    }
  }

  async function handleSubmitScore(criticName: string, score: number) {
    if (!scoringWine) return;
    
    try {
      // Check if score already exists for this critic
      const { data: existing } = await supabase
        .from('wine_critic_scores')
        .select('id')
        .eq('wine_id', scoringWine.id)
        .eq('critic_name', criticName)
        .single();
      
      if (existing) {
        // Update existing score
        const { error } = await supabase
          .from('wine_critic_scores')
          .update({ score: score })
          .eq('id', existing.id);
        
        if (error) throw error;
        showToastMessage(`Updated ${criticName} score`);
      } else {
        // Insert new score
        const { error } = await supabase
          .from('wine_critic_scores')
          .insert([{
            wine_id: scoringWine.id,
            critic_name: criticName,
            score: score
          }]);
        
        if (error) throw error;
        showToastMessage(`Added ${criticName} score`);
      }
      
      // Reload wines
      await loadWines();
      
      // Update scoringWine with fresh data so modal shows updated scores
      const { data: freshScores } = await supabase
        .from('wine_critic_scores')
        .select('*')
        .eq('wine_id', scoringWine.id);
      
      setScoringWine({
        ...scoringWine,
        critic_scores: freshScores || []
      });
    } catch (error) {
      console.error('Error saving score:', error);
      alert('Error saving score: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async function handleDeleteScore(scoreId: string) {
    if (!scoringWine) return;
    if (!confirm('Remove this score?')) return;
    
    try {
      const { error } = await supabase
        .from('wine_critic_scores')
        .delete()
        .eq('id', scoreId);
      
      if (error) throw error;
      
      await loadWines();
      
      // Update scoringWine with fresh data so modal shows updated scores
      const { data: freshScores } = await supabase
        .from('wine_critic_scores')
        .select('*')
        .eq('wine_id', scoringWine.id);
      
      setScoringWine({
        ...scoringWine,
        critic_scores: freshScores || []
      });
      
      showToastMessage('Score removed');
    } catch (error) {
      console.error('Error deleting score:', error);
      alert('Error deleting score: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async function handleUpdateMyRating(rating: number | null) {
    if (!scoringWine) return;
    
    try {
      const { error } = await supabase
        .from('wines')
        .update({ my_rating: rating })
        .eq('id', scoringWine.id);
      
      if (error) throw error;
      
      await loadWines();
      showToastMessage(rating ? `My rating: ${rating}` : 'Rating removed');
      
      // Recalculate CellarLog rating
      handleAddScore(scoringWine);
    } catch (error) {
      console.error('Error updating rating:', error);
      alert('Error updating rating: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }
  
  // Effect to update scoringWine and selectedWine when wines change
  useEffect(() => {
    if (scoringWine) {
      const updatedWine = wines.find(w => w.id === scoringWine.id);
      if (updatedWine) {
        setScoringWine(updatedWine);
      }
    }
    if (selectedWine) {
      const updatedWine = wines.find(w => w.id === selectedWine.id);
      if (updatedWine) {
        setSelectedWine(updatedWine);
      }
    }
  }, [wines]);

  function clearFilters() {
    setSearchTerm('');
    setFilterColor('');
    setFilterVarietal('');
    setFilterCountry('');
    setSortBy('name');
  }

  function resetWineForm() {
    setWineForm(initialWineForm);
    setEditingWine(null);
    setShowWineForm(false);
  }

  function handleEditWine(wine: Wine) {
    setWineForm({
      wine_name: wine.wine_name || '',
      producer: wine.producer || '',
      vintage: wine.vintage ? String(wine.vintage) : '',
      color: wine.color || 'red',
      wine_type: wine.wine_type || 'still',
      country: wine.country || '',
      region: wine.region || '',
      appellation: wine.appellation || '',
      varietal: wine.varietal || '',
      bottle_size: wine.bottle_size || '750ml',
      quantity: wine.quantity || 1,
      purchase_price: wine.purchase_price ? String(wine.purchase_price) : '',
      cellar_location: wine.cellar_location || '',
      drink_from: wine.drink_from ? String(wine.drink_from) : '',
      drink_to: wine.drink_to ? String(wine.drink_to) : '',
      my_rating: wine.my_rating ? String(wine.my_rating) : '',
      tasting_notes: wine.tasting_notes || ''
    });
    setEditingWine(wine);
    setShowWineForm(true);
  }

  async function handleDeleteWine(id: string) {
    if (confirm('Remove this wine from your cellar?')) {
      try {
        const { error } = await supabase
          .from('wines')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        await loadWines();
        showToastMessage('Wine removed');
      } catch (error) {
        console.error('Error deleting wine:', error);
        alert('Error deleting wine: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    }
  }

  async function handleUpdateQuantity(id: string, delta: number) {
    try {
      const wine = wines.find(w => w.id === id);
      if (!wine) return;
      
      const newQuantity = Math.max(0, wine.quantity + delta);
      
      const { error } = await supabase
        .from('wines')
        .update({ quantity: newQuantity })
        .eq('id', id);
      
      if (error) throw error;
      await loadWines();
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  function handleDrinkWine(wine: Wine) {
    setDrinkingWine(wine);
    setDrinkForm({
      date_drank: new Date().toISOString().split('T')[0],
      occasion_notes: '',
      rating: wine.my_rating ? String(wine.my_rating) : '',
      quantity: 1
    });
    setShowDrinkModal(true);
  }

  async function handleConfirmDrink(e: FormEvent) {
    e.preventDefault();
    if (!drinkingWine || !currentCellar || !user) return;

    try {
      // Create history entry (one entry even for multiple bottles)
      const historyEntry = {
        cellar_id: currentCellar.id,
        user_id: user.id,
        original_wine_id: drinkingWine.id,
        canonical_wine_id: drinkingWine.canonical_wine_id || null,
        wine_name: drinkingWine.wine_name || drinkingWine.producer || 'Unknown Wine',
        producer: drinkingWine.producer || null,
        vintage: drinkingWine.vintage || null,
        color: drinkingWine.color || 'red',
        wine_type: drinkingWine.wine_type || 'still',
        country: drinkingWine.country || null,
        region: drinkingWine.region || null,
        appellation: drinkingWine.appellation || null,
        varietal: drinkingWine.varietal || null,
        bottle_size: drinkingWine.bottle_size || '750ml',
        purchase_price: drinkingWine.purchase_price || null,
        date_drank: drinkForm.date_drank,
        occasion_notes: drinkForm.quantity > 1 
          ? `${drinkForm.occasion_notes ? drinkForm.occasion_notes + ' ' : ''}(${drinkForm.quantity} bottles)`
          : (drinkForm.occasion_notes || null),
        rating: drinkForm.rating ? parseFloat(drinkForm.rating) : null
      };

      const { error: historyError } = await supabase
        .from('wine_history')
        .insert([historyEntry]);

      if (historyError) throw historyError;

      // Save user's personal rating if provided
      if (drinkForm.rating && drinkingWine.canonical_wine_id) {
        await saveUserRating(drinkingWine.canonical_wine_id, parseFloat(drinkForm.rating));
      }

      const newQuantity = drinkingWine.quantity - drinkForm.quantity;

      // Update wine quantity or delete if none left
      if (newQuantity > 0) {
        const { error: updateError } = await supabase
          .from('wines')
          .update({ quantity: newQuantity })
          .eq('id', drinkingWine.id);
        
        if (updateError) throw updateError;
      } else {
        const { error: deleteError } = await supabase
          .from('wines')
          .delete()
          .eq('id', drinkingWine.id);
        
        if (deleteError) throw deleteError;
      }

      await loadWines();
      await loadWineHistory();
      
      setShowDrinkModal(false);
      setDrinkingWine(null);
      const bottleText = drinkForm.quantity > 1 ? `${drinkForm.quantity} bottles of ` : '';
      const displayName = getWineDisplayName(drinkingWine);
      showToastMessage(`🍷 Enjoyed ${bottleText}${displayName}!`);
    } catch (error) {
      console.error('Error recording drink:', error);
      alert('Error recording drink: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  function handleAddFromHistory(entry: GroupedHistoryEntry) {
    // Store canonical_wine_id if available so we preserve ratings when adding back
    setAddFromHistoryCanonicalId(entry.canonical_wine_id || null);
    
    setWineForm({
      wine_name: entry.wine_name || '',
      producer: entry.producer || '',
      vintage: entry.vintage ? String(entry.vintage) : '',
      color: (entry.color as WineFormData['color']) || 'red',
      wine_type: (entry.wine_type as WineFormData['wine_type']) || 'still',
      country: entry.country || '',
      region: entry.region || '',
      appellation: entry.appellation || '',
      varietal: entry.varietal || '',
      bottle_size: entry.bottle_size || '750ml',
      quantity: 1,
      purchase_price: entry.purchase_price ? String(entry.purchase_price) : '',
      cellar_location: '',
      drink_from: '',
      drink_to: '',
      my_rating: '',
      tasting_notes: ''
    });
    setEditingWine(null);
    setShowHistory(false);
    setShowWineForm(true);
  }

  async function handleViewHistoryWine(canonicalWineId: string) {
    try {
      // Fetch the canonical wine with its critic scores
      const { data: canonicalWine, error } = await supabase
        .from('canonical_wines')
        .select(`
          *,
          critic_scores:canonical_critic_scores(*)
        `)
        .eq('id', canonicalWineId)
        .single();
      
      if (error) throw error;
      
      if (canonicalWine) {
        setViewingHistoryWine(canonicalWine);
      }
    } catch (error) {
      console.error('Error fetching canonical wine:', error);
      showToastMessage('Could not load wine details', 'error');
    }
  }

  // Canonical wine flow handlers
  function handleSelectCanonicalWine(wine: CanonicalWine) {
    setSelectedCanonicalWine(wine);
    setShowWineSearch(false);
    setShowQuickAdd(true);
  }

  function handleCreateNewWine(searchTerm: string) {
    // Pre-fill the form with search term as wine name
    setWineForm({
      ...initialWineForm,
      wine_name: searchTerm
    });
    setEditingWine(null);
    setShowWineSearch(false);
    setShowWineForm(true);
  }

  async function handleQuickAdd(wine: CanonicalWine, formData: QuickAddFormData) {
    if (!currentCellar || !user) return;

    try {
      // Check for existing wine - either by canonical_wine_id or by name/producer/vintage match
      const existingWine = wines.find(w => {
        // Match by canonical wine ID
        if (w.canonical_wine_id && w.canonical_wine_id === wine.id) {
          return true;
        }
        // Match by name/producer/vintage (for pre-migration wines)
        const nameMatch = (w.wine_name || '').toLowerCase().trim() === (wine.wine_name || '').toLowerCase().trim();
        const producerMatch = (w.producer || '').toLowerCase().trim() === (wine.producer || '').toLowerCase().trim();
        const vintageMatch = (w.vintage || 0) === (wine.vintage || 0);
        return nameMatch && producerMatch && vintageMatch;
      });

      if (existingWine) {
        const newTotal = existingWine.quantity + formData.quantity;
        const choice = window.confirm(
          `This wine already exists in your cellar:\n\n` +
          `${existingWine.wine_name} ${existingWine.vintage || ''}\n` +
          `Current quantity: ${existingWine.quantity} bottle${existingWine.quantity > 1 ? 's' : ''}\n\n` +
          `Add ${formData.quantity} bottle${formData.quantity > 1 ? 's' : ''} to existing entry (total: ${newTotal})?\n\n` +
          `Click OK to add to existing, or Cancel to create a new entry.`
        );

        if (choice) {
          // Update existing wine quantity, price, and link to canonical if not already
          const updateData: { quantity: number; canonical_wine_id?: string; purchase_price?: number | null } = { 
            quantity: newTotal 
          };
          if (!existingWine.canonical_wine_id) {
            updateData.canonical_wine_id = wine.id;
          }
          // Update price if provided
          if (formData.purchase_price) {
            updateData.purchase_price = parseFloat(formData.purchase_price);
          }
          
          const { error } = await supabase
            .from('wines')
            .update(updateData)
            .eq('id', existingWine.id);
          
          if (error) throw error;
          
          await loadWines();
          setShowQuickAdd(false);
          setSelectedCanonicalWine(null);
          showToastMessage(`Added ${formData.quantity} bottle${formData.quantity > 1 ? 's' : ''} (now ${newTotal} total)`);
          return;
        }
      }

      // Create new wine entry linked to canonical wine
      const wineEntry = {
        cellar_id: currentCellar.id,
        canonical_wine_id: wine.id,
        wine_name: wine.wine_name,
        producer: wine.producer,
        vintage: wine.vintage,
        color: wine.color,
        wine_type: wine.wine_type,
        country: wine.country,
        region: wine.region,
        appellation: wine.appellation,
        varietal: wine.varietal,
        bottle_size: wine.bottle_size,
        quantity: formData.quantity,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        cellar_location: formData.cellar_location || null,
        my_rating: formData.my_rating ? parseInt(formData.my_rating) : null,
        tasting_notes: formData.tasting_notes || null
      };

      const { error } = await supabase
        .from('wines')
        .insert([wineEntry]);

      if (error) throw error;

      await loadWines();
      setShowQuickAdd(false);
      setSelectedCanonicalWine(null);
      showToastMessage(`Added ${wine.wine_name} to your cellar!`);
    } catch (error: any) {
      console.error('Error adding wine:', error);
      const message = error?.message || error?.error_description || JSON.stringify(error);
      alert('Error adding wine: ' + message);
    }
  }

  function handleScanFromSearch() {
    // Close search and open form with scan capability
    setShowWineSearch(false);
    setEditingWine(null);
    setWineForm(initialWineForm);
    setShowWineForm(true);
    // Note: The scan button is in WineForm, so just opening the form is enough
  }

  async function handleDeleteHistoryOccasion(occasion: HistoryOccasion) {
    console.log('[History Delete] Starting delete for occasion:', occasion.id);
    
    const dateStr = new Date(occasion.date_drank).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const confirmed = window.confirm(
      `Delete this drinking occasion?\n\n${dateStr}${occasion.occasion_notes ? '\n"' + occasion.occasion_notes + '"' : ''}\n\nThis cannot be undone.`
    );
    
    if (!confirmed) {
      console.log('[History Delete] User cancelled');
      return;
    }

    try {
      console.log('[History Delete] Attempting delete from wine_history table...');
      
      const { data, error, count } = await supabase
        .from('wine_history')
        .delete()
        .eq('id', occasion.id)
        .select();
      
      console.log('[History Delete] Delete result:', { data, error, count });
      
      if (error) throw error;
      
      console.log('[History Delete] Delete successful, reloading history...');
      await loadWineHistory();
      console.log('[History Delete] History reloaded, wineHistory count:', wineHistory.length);
      
      showToastMessage('History entry deleted');
    } catch (error) {
      console.error('[History Delete] Error:', error);
      alert('Error deleting history: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async function handleInviteMember(email: string) {
    if (!currentCellar || !user) return;

    try {
      const { data: existing } = await supabase
        .from('cellar_invitations')
        .select('*')
        .eq('cellar_id', currentCellar.id)
        .eq('invitee_email', email)
        .single();
      
      if (existing) {
        alert(`An invitation has already been sent to ${email} for this cellar (Status: ${existing.status})`);
        return;
      }

      const { error } = await supabase
        .from('cellar_invitations')
        .insert([{
          cellar_id: currentCellar.id,
          inviter_id: user.id,
          invitee_email: email,
          status: 'pending'
        }]);
      
      if (error) throw error;
      
      await loadCellarPendingInvites();
      showToastMessage(`Invitation sent to ${email}!`);
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Error sending invite: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async function handleAcceptInvitation(invitation: CellarInvitation) {
    if (!user) return;

    try {
      const { error: memberError } = await supabase
        .from('cellar_members')
        .insert([{
          cellar_id: invitation.cellar_id,
          user_id: user.id,
          role: 'member'
        }]);
      
      if (memberError) throw memberError;

      const { error: updateError } = await supabase
        .from('cellar_invitations')
        .update({ status: 'accepted' })
        .eq('id', invitation.id);
      
      if (updateError) throw updateError;

      await loadCellars(user);
      await loadPendingInvitations(user);
      setShowPendingInvites(false);
      showToastMessage('Invitation accepted!');
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Error accepting invitation: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async function handleDeclineInvitation(invitation: CellarInvitation) {
    try {
      const { error } = await supabase
        .from('cellar_invitations')
        .update({ status: 'declined' })
        .eq('id', invitation.id);
      
      if (error) throw error;
      
      if (user) {
        await loadPendingInvitations(user);
      }
      showToastMessage('Invitation declined');
    } catch (error) {
      console.error('Error declining invitation:', error);
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#2a2520' }}>
        <div className="text-2xl wine-title" style={{ color: '#d4af37' }}>Loading...</div>
      </div>
    );
  }

  // Show refresh message only if auto-refresh already failed
  if (showRefreshMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#2a2520' }}>
        <div className="text-center p-8">
          <div className="text-xl wine-title mb-4" style={{ color: '#d4af37' }}>
            Connection Issue
          </div>
          <p className="mb-6" style={{ color: '#c9b998' }}>
            Having trouble connecting. Please try again.
          </p>
          <button
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              if ('caches' in window) {
                caches.keys().then(names => names.forEach(name => caches.delete(name)));
              }
              window.location.href = window.location.origin;
            }}
            className="px-6 py-3 rounded text-white"
            style={{ background: '#722f37' }}
          >
            Reload App
          </button>
        </div>
      </div>
    );
  }

  // Auth form
  if (!user) {
    return <AuthForm onAuthSuccess={() => {}} />;
  }

  // Cellar selection view
  if (!currentCellar) {
    return (
      <div className="min-h-screen no-pull-refresh safe-top safe-bottom" style={{ background: 'var(--color-parchment)' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                Welcome{username ? `, ${username}` : user?.email ? `, ${user.email.split('@')[0]}` : ''}
              </p>
              <h1 className="ios-header-title">Your Cellars</h1>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {pendingInvitations.length > 0 && (
                <button
                  onClick={() => setShowPendingInvites(true)}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    background: '#ea580c',
                    color: 'white',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {pendingInvitations.length}
                </button>
              )}
              <button
                onClick={() => setShowProfileModal(true)}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  background: 'var(--color-parchment-dark)',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
                title="Profile"
              >
                👤
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '0 16px 100px' }}>
          {/* New Cellar Button */}
          <button
            onClick={() => setShowCellarForm(true)}
            className="ios-btn ios-btn-primary w-full"
            style={{ marginBottom: '16px' }}
          >
            + New Cellar
          </button>

          <CellarList
            cellars={cellars}
            currentUserId={user.id}
            onSelectCellar={setCurrentCellar}
          />
        </div>

        {showCellarForm && (
          <CellarForm
            cellarForm={cellarForm}
            setCellarForm={setCellarForm}
            editingCellar={editingCellar}
            onSubmit={handleSubmitCellar}
            onDelete={editingCellar ? handleDeleteCellar : undefined}
            onClose={() => { setShowCellarForm(false); setCellarForm(initialCellarForm); setEditingCellar(null); }}
          />
        )}

        {showPendingInvites && (
          <PendingInvitations
            invitations={pendingInvitations}
            onAccept={handleAcceptInvitation}
            onDecline={handleDeclineInvitation}
            onClose={() => setShowPendingInvites(false)}
          />
        )}

        {showProfileModal && user && (
          <ProfileModal
            user={user}
            cellars={cellars}
            onClose={() => setShowProfileModal(false)}
            onProfileUpdated={() => {}}
            onDeleteAccount={handleDeleteAccount}
            onSignOut={handleSignOut}
          />
        )}

        <Toast toast={toast} />
      </div>
    );
  }

  // Main cellar view with bottom tabs
  return (
    <div className="min-h-screen no-pull-refresh" style={{ background: 'var(--color-parchment)' }}>
      {/* Tab Content */}
      {activeTab === 'cellar' && (
        <div className="main-content safe-top">
          {/* Header */}
          <div style={{ 
            padding: '12px 16px 8px',
            borderBottom: '1px solid var(--color-border)'
          }}>
            {/* Top row: back + actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <button
                onClick={() => setCurrentCellar(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--color-wine)', 
                  fontSize: '15px',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                ← Cellars
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={handlePageRefresh}
                  disabled={isPageRefreshing}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    opacity: isPageRefreshing ? 0.5 : 1
                  }}
                  title="Refresh"
                >
                  <span className={isPageRefreshing ? 'animate-spin' : ''}>🔄</span>
                </button>
                <button
                  onClick={() => setShowMembersPanel(true)}
                  style={{
                    background: 'var(--color-parchment-dark)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '14px',
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}
              >
                👥 {members.length > 1 && <span>{members.length}</span>}
              </button>
              <button
                onClick={() => setShowProfileModal(true)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '16px',
                  background: 'var(--color-wine)',
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Profile & Settings"
              >
                👤
              </button>
              </div>
            </div>

            {/* Cellar name + stats */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h1 className="ios-header-title" style={{ margin: 0 }}>
                  {currentCellar.name}
                </h1>
                {isOwner && (
                  <button
                    onClick={() => {
                      setCellarForm({ name: currentCellar.name, description: currentCellar.description || '' });
                      setEditingCellar(currentCellar);
                      setShowCellarForm(true);
                    }}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      opacity: 0.5,
                      cursor: 'pointer'
                    }}
                    title="Edit cellar"
                  >
                    ✏️
                  </button>
                )}
              </div>
              <p className="ios-header-subtitle">
                {stats.total} bottles · ${stats.value.toFixed(0)} value
                {currentCellar.owner_id !== user.id && (
                  <span className="badge badge-blue" style={{ marginLeft: '8px' }}>
                    Shared
                  </span>
                )}
              </p>
            </div>

            {/* Search + Filter row */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="🔍 Search wines..."
                  className="ios-input"
                  style={{ paddingRight: searchTerm ? '32px' : '14px' }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer',
                      fontSize: '18px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowFilterDrawer(true)}
                style={{
                  position: 'relative',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-card)',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span>☰</span>
                {activeFilterCount > 0 && (
                  <span 
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      width: '18px',
                      height: '18px',
                      borderRadius: '9px',
                      background: 'var(--color-wine)',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* Results count */}
            <div style={{ 
              fontSize: '13px', 
              color: 'var(--color-text-muted)',
              marginTop: '8px'
            }}>
              {filteredWines.length} {filteredWines.length === 1 ? 'wine' : 'wines'}
              {activeFilterCount > 0 && (
                <button 
                  onClick={clearFilters} 
                  style={{ 
                    marginLeft: '8px', 
                    textDecoration: 'underline', 
                    color: 'var(--color-wine)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Wine List */}
          <div style={{ padding: '12px 16px', paddingBottom: '100px' }}>
            <WineList
              wines={filteredWines}
              onSelectWine={(wine) => setSelectedWine(wine)}
            />
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="safe-top">
          <HistoryTab
            groupedHistory={groupedHistory}
            onViewWine={handleViewHistoryWine}
            onAddFromHistory={handleAddFromHistory}
            onDeleteOccasion={handleDeleteHistoryOccasion}
          />
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="safe-top">
          <StatsTab
            wines={wines}
            groupedHistory={groupedHistory}
            cellarName={currentCellar.name}
          />
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="safe-top">
          <ProfileTab
            user={user}
            username={username}
            cellars={cellars}
            wines={wines}
            pendingInvitations={pendingInvitations}
            onShowInvitations={() => setShowPendingInvites(true)}
            onSignOut={handleSignOut}
            onDeleteAccount={handleDeleteAccount}
            onUsernameUpdated={(newUsername) => setUsername(newUsername)}
          />
        </div>
      )}

      {/* Bottom Tab Bar */}
      <BottomTabBar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        pendingInvitesCount={pendingInvitations.length}
      />

      {/* FAB for adding wine - only on cellar tab */}
      {activeTab === 'cellar' && (
        <button
          onClick={() => setShowWineSearch(true)}
          className="fab"
          title="Add Wine"
        >
          +
        </button>
      )}

      {/* Modals */}
      {showWineSearch && (
        <WineSearchScreen
          onSelectWine={handleSelectCanonicalWine}
          onCreateNew={handleCreateNewWine}
          onScan={handleScanFromSearch}
          onClose={() => setShowWineSearch(false)}
        />
      )}

      {showQuickAdd && selectedCanonicalWine && (
        <QuickAddModal
          wine={selectedCanonicalWine}
          onSubmit={handleQuickAdd}
          onBack={() => {
            setShowQuickAdd(false);
            setSelectedCanonicalWine(null);
            setShowWineSearch(true);
          }}
          onClose={() => {
            setShowQuickAdd(false);
            setSelectedCanonicalWine(null);
          }}
        />
      )}

      {showCellarForm && (
        <CellarForm
          cellarForm={cellarForm}
          setCellarForm={setCellarForm}
          editingCellar={editingCellar}
          onSubmit={handleSubmitCellar}
          onDelete={editingCellar ? handleDeleteCellar : undefined}
          onClose={() => { setShowCellarForm(false); setCellarForm(initialCellarForm); setEditingCellar(null); }}
        />
      )}

      {showWineForm && (
        <WineForm
          wineForm={wineForm}
          setWineForm={setWineForm}
          editingWine={editingWine}
          onSubmit={handleSubmitWine}
          onDelete={editingWine ? () => {
            handleDeleteWine(editingWine.id);
            resetWineForm();
            setSelectedWine(null);
          } : undefined}
          onClose={resetWineForm}
          suggestions={cellarSuggestions}
        />
      )}

      {showDrinkModal && drinkingWine && (
        <DrinkModal
          wine={drinkingWine}
          drinkForm={drinkForm}
          setDrinkForm={setDrinkForm}
          onSubmit={handleConfirmDrink}
          onClose={() => { setShowDrinkModal(false); setDrinkingWine(null); }}
        />
      )}

      {scoringWine && (
        <AddScoreModal
          wine={scoringWine}
          cellarLogRating={cellarLogRating}
          onSubmit={handleSubmitScore}
          onDelete={handleDeleteScore}
          onUpdateMyRating={handleUpdateMyRating}
          onClose={() => { setScoringWine(null); setCellarLogRating(null); }}
        />
      )}

      {showHistory && (
        <HistoryModal
          wineHistory={wineHistory}
          onAddFromHistory={handleAddFromHistory}
          onDeleteOccasion={handleDeleteHistoryOccasion}
          onViewWine={handleViewHistoryWine}
          onClose={() => setShowHistory(false)}
        />
      )}

      {viewingHistoryWine && (
        <CanonicalWineDetail
          wine={viewingHistoryWine}
          onClose={() => setViewingHistoryWine(null)}
        />
      )}

      {showMembersPanel && (
        <MembersPanel
          members={members}
          pendingInvites={cellarPendingInvites}
          currentUserId={user.id}
          ownerId={currentCellar.owner_id}
          onInvite={handleInviteMember}
          onClose={() => setShowMembersPanel(false)}
        />
      )}

      {showProfileModal && user && (
        <ProfileModal
          user={user}
          cellars={cellars}
          onClose={() => setShowProfileModal(false)}
          onProfileUpdated={() => {}}
          onDeleteAccount={handleDeleteAccount}
          onSignOut={handleSignOut}
        />
      )}

      {showFilterDrawer && (
        <FilterDrawer
          filterColor={filterColor}
          filterVarietal={filterVarietal}
          filterCountry={filterCountry}
          sortBy={sortBy}
          varietals={filterOptions.varietals}
          countries={filterOptions.countries}
          resultCount={filteredWines.length}
          onColorChange={setFilterColor}
          onVarietalChange={setFilterVarietal}
          onCountryChange={setFilterCountry}
          onSortChange={setSortBy}
          onClear={clearFilters}
          onClose={() => setShowFilterDrawer(false)}
        />
      )}

      {selectedWine && (
        <WineDetail
          wine={selectedWine}
          isRefreshing={refreshingRatings}
          onEdit={() => {
            handleEditWine(selectedWine);
            setSelectedWine(null);
          }}
          onDrink={() => {
            handleDrinkWine(selectedWine);
            setSelectedWine(null);
          }}
          onAddScore={() => {
            const wine = selectedWine;
            setSelectedWine(null);
            handleAddScore(wine);
          }}
          onUpdateQuantity={(delta) => {
            handleUpdateQuantity(selectedWine.id, delta);
          }}
          onRefreshRatings={() => handleRefreshRatings(selectedWine)}
          onSaveUserRating={async (rating: number) => {
            if (!selectedWine.canonical_wine_id) {
              throw new Error('No canonical wine ID');
            }
            await saveUserRating(selectedWine.canonical_wine_id, rating);
            await loadWines();
            // Update the selectedWine with new rating
            setSelectedWine(prev => prev ? { ...prev, user_rating: rating } : null);
            showToastMessage('Rating saved');
          }}
          onClose={() => setSelectedWine(null)}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}
